import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getWorkers } from '../../../redux/features/workerSlice';
import { getInventoryItems } from '../../../redux/api';
import { Plus, RotateCcw, Loader2, User, Package, CheckCircle, XCircle, Clock, ChevronDown, ChevronRight, Trash2, Search } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';
import { su } from '../../inventory/stockUi';

const REVERS_SOURCE = 'INVENTORY_ITEM';

const ReversiView = () => {
  const dispatch = useDispatch();
  const { workers, loading: workersLoading } = useSelector((state) => state.worker || { workers: [] });
  const [reversi, setReversi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedWorkerFilter, setSelectedWorkerFilter] = useState('');
  const [reversSearchTerm, setReversSearchTerm] = useState('');
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [itemSearchVisible, setItemSearchVisible] = useState(false);
  const [itemSearchFocused, setItemSearchFocused] = useState(false);
  const [expandedWorkers, setExpandedWorkers] = useState({});
  const [reversParts, setReversParts] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [formData, setFormData] = useState({
    workerId: '',
    itemId: '',
    quantity: 1,
    notes: ''
  });

  // Check if user is a worker
  const isWorker = typeof window !== 'undefined' ? localStorage.getItem('userType') === 'worker' : false;

  useEffect(() => {
    fetchReversi();
    dispatch(getWorkers());
    fetchInventoryItems();
  }, [dispatch]);

  const fetchInventoryItems = async () => {
    try {
      setInventoryLoading(true);
      const response = await getInventoryItems({ stockKind: 'WAREHOUSE' });
      setInventoryItems(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching inventory items:', error);
    } finally {
      setInventoryLoading(false);
    }
  };

  const fetchReversi = async () => {
    try {
      setLoading(true);
      const profile = JSON.parse(localStorage.getItem('profile') || '{}');
      const token = profile?.token;
      
      const response = await axios.get(`${API_BASE_URL}/api/inventory/reversi`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReversi(response.data || []);
    } catch (error) {
      console.error('Error fetching reversi:', error);
      toast.error('Greška pri učitavanju reversi zapisa');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.workerId) {
      toast.error('Izaberite radnika');
      return;
    }

    if (!reversParts.length) {
      toast.error('Dodajte bar jedan deo u revers');
      return;
    }

    try {
      const profile = JSON.parse(localStorage.getItem('profile') || '{}');
      const token = profile?.token;

      await axios.post(
        `${API_BASE_URL}/api/inventory/reversi`,
        {
          workerId: formData.workerId,
          notes: formData.notes,
          parts: reversParts.map((p) => ({
            sourceType: REVERS_SOURCE,
            inventoryItemId: p.inventoryItemId,
            quantity: p.quantity,
          })),
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Revers uspešno kreiran');
      setShowForm(false);
      setFormData({ workerId: '', itemId: '', quantity: 1, notes: '' });
      setReversParts([]);
      setItemSearchTerm('');
      fetchReversi();
      fetchInventoryItems();
    } catch (error) {
      console.error('Error creating revers:', error);
      const errorMessage = error.response?.data?.message || 'Greška pri kreiranju reversa';
      toast.error(errorMessage);
    }
  };

  const handleAddPartToRevers = () => {
    const selectedPartId = formData.itemId;
    const qty = Number(formData.quantity);

    if (!selectedPartId) {
      toast.error('Izaberite artikal iz magacina');
      return;
    }
    if (!Number.isFinite(qty) || qty < 1) {
      toast.error('Unesite ispravnu količinu');
      return;
    }

    const partKey = `${REVERS_SOURCE}:${selectedPartId}`;
    const selectedPart = inventoryItems.find(
      (p) => (p._id || p.id)?.toString() === selectedPartId.toString()
    );

    if (!selectedPart) {
      toast.error('Artikal nije pronađen u magacinu');
      return;
    }

    const stockQty = Number(selectedPart.availableQuantity ?? selectedPart.currentQuantity ?? 0);

    const alreadyAddedQty = reversParts
      .filter((p) => `${REVERS_SOURCE}:${p.inventoryItemId?.toString()}` === partKey)
      .reduce((sum, p) => sum + (Number(p.quantity) || 0), 0);

    if (alreadyAddedQty + qty > stockQty) {
      toast.error(`Nedovoljna količina. Na stanju je ${stockQty}, već dodato ${alreadyAddedQty}, pokušavate +${qty}.`);
      return;
    }

    const existingEntry = reversParts.find(
      (p) => `${REVERS_SOURCE}:${p.inventoryItemId?.toString()}` === partKey
    );

    setReversParts((prev) => {
      const idx = prev.findIndex(
        (p) => `${REVERS_SOURCE}:${p.inventoryItemId?.toString()}` === partKey
      );
      if (idx === -1) {
        return [
          ...prev,
          {
            sourceType: REVERS_SOURCE,
            inventoryItemId: selectedPartId,
            quantity: qty,
            name: selectedPart.name || 'Nepoznat artikal',
            code: selectedPart.itemCode || '',
          },
        ];
      }
      const next = [...prev];
      next[idx] = { ...next[idx], quantity: Number(next[idx].quantity || 0) + qty };
      return next;
    });

    const partLabel = selectedPart.name || 'Artikal';
    if (existingEntry) {
      const newTotal = Number(existingEntry.quantity || 0) + qty;
      toast.success(`${partLabel} ažuriran u reversu — ukupno ${newTotal} kom`);
    } else {
      toast.success(`${partLabel} dodat u revers (${qty} kom)`);
    }

    setFormData((prev) => ({ ...prev, itemId: '', quantity: 1 }));
    setItemSearchTerm('');
  };

  const updateReversPartQuantity = (partKey, nextQuantity) => {
    const qty = Number(nextQuantity);
    if (!Number.isFinite(qty) || qty < 1) return;

    const entry = reversParts.find(
      (p) => `${REVERS_SOURCE}:${p.inventoryItemId?.toString()}` === partKey
    );
    if (!entry) return;

    const stockPart = inventoryItems.find(
      (p) => (p._id || p.id)?.toString() === entry.inventoryItemId?.toString()
    );
    const stockQty = Number(stockPart?.availableQuantity ?? stockPart?.currentQuantity ?? 0);
    if (qty > stockQty) return;

    setReversParts((prev) =>
      prev.map((p) =>
        `${REVERS_SOURCE}:${p.inventoryItemId?.toString()}` === partKey
          ? { ...p, quantity: qty }
          : p
      )
    );
  };

  const removeReversPart = (partKey) => {
    setReversParts((prev) =>
      prev.filter((p) => `${REVERS_SOURCE}:${p.inventoryItemId?.toString()}` !== partKey)
    );
  };

  const handleReturn = async (reversId) => {
    if (!window.confirm('Da li ste sigurni da želite da vratite ovaj deo na stanje?')) {
      return;
    }

    try {
      const profile = JSON.parse(localStorage.getItem('profile') || '{}');
      const token = profile?.token;
      
      await axios.put(`${API_BASE_URL}/api/inventory/reversi/${reversId}/return`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Deo je uspešno vraćen na stanje');
      fetchReversi();
      fetchInventoryItems();
    } catch (error) {
      console.error('Error returning revers:', error);
      toast.error(error.response?.data?.message || 'Greška pri vraćanju dela');
    }
  };

  const handleReassign = async (reversId) => {
    if (!window.confirm('Da li ste sigurni da želite da ponovo zadužite ovaj deo radniku?')) {
      return;
    }

    try {
      const profile = JSON.parse(localStorage.getItem('profile') || '{}');
      const token = profile?.token;

      await axios.post(`${API_BASE_URL}/api/inventory/reversi/${reversId}/reassign`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Deo je ponovo zadužen radniku');
      fetchReversi();
      fetchInventoryItems();
    } catch (error) {
      console.error('Error re-assigning revers:', error);
      toast.error(error.response?.data?.message || 'Greška pri ponovnom zaduženju');
    }
  };

  const handleDelete = async (reversId) => {
    if (!window.confirm('Da li ste sigurni da želite trajno da obrišete ovaj revers zapis?')) {
      return;
    }

    try {
      const profile = JSON.parse(localStorage.getItem('profile') || '{}');
      const token = profile?.token;

      await axios.delete(`${API_BASE_URL}/api/inventory/reversi/${reversId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Revers zapis je obrisan');
      fetchReversi();
    } catch (error) {
      console.error('Error deleting revers:', error);
      toast.error(error.response?.data?.message || 'Greška pri brisanju reversa');
    }
  };

  const handleDeleteUnknownWorkerRevers = async (workerId) => {
    if (!window.confirm('Nisu svi delovi razduženi. Da li želite da vratite sve delove na stanje i očistite revers zapise za "Nepoznat radnik"?')) {
      return;
    }

    try {
      const profile = JSON.parse(localStorage.getItem('profile') || '{}');
      const token = profile?.token;

      const response = await axios.post(`${API_BASE_URL}/api/inventory/reversi/worker/${workerId}/return-all-cleanup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(response?.data?.message || 'Delovi su vraćeni na stanje i revers zapisi očišćeni');
      fetchReversi();
      fetchInventoryItems();
    } catch (error) {
      console.error('Error deleting unknown worker revers:', error);
      toast.error(error.response?.data?.message || 'Greška pri brisanju zapisa');
    }
  };

  const getWorkerName = (workerId) => {
    // Handle populated object from server
    if (typeof workerId === 'object' && workerId !== null) {
      return `${workerId.firstName || ''} ${workerId.lastName || ''}`.trim() || workerId.email || 'Nepoznat radnik';
    }
    // Handle string ID - find in workers array
    const worker = workers.find(w => w._id === workerId || w.id === workerId);
    return worker ? `${worker.firstName || ''} ${worker.lastName || ''}`.trim() || worker.email : 'Nepoznat radnik';
  };

  const getReversItemInfo = (revers) => {
    if (revers?.item) {
      return {
        name: revers.item.name || 'Nepoznata stavka',
        code: revers.item.code || '',
        category: revers.item.category || '',
        sourceLabel: 'Magacin',
      };
    }
    if (revers?.inventoryItemId && typeof revers.inventoryItemId === 'object') {
      return {
        name: revers.inventoryItemId.name || 'Artikal',
        code: revers.inventoryItemId.itemCode || '',
        category: revers.inventoryItemId.category || '',
        sourceLabel: 'Magacin',
      };
    }
    if (revers?.sparePartId && typeof revers.sparePartId === 'object') {
      return {
        name: revers.sparePartId.name || 'Rezervni deo',
        code: revers.sparePartId.code || '',
        category: revers.sparePartId.category || '',
        sourceLabel: 'Rez. delovi (staro)',
      };
    }
    return {
      name: 'Nepoznata stavka',
      code: '',
      category: '',
      sourceLabel: 'Magacin',
    };
  };

  const getWorkerIdFromRevers = (revers) => {
    // Handle populated object from server
    if (typeof revers.workerId === 'object' && revers.workerId !== null) {
      return revers.workerId._id || revers.workerId.id;
    }
    // Handle string ID
    return revers.workerId;
  };

  // Filter reversi by selected worker (only for admin, workers already see only their own)
  const filteredReversi = isWorker
    ? reversi // Workers already get filtered reversi from backend
    : selectedWorkerFilter
    ? reversi.filter(revers => {
        const workerId = getWorkerIdFromRevers(revers);
        return workerId === selectedWorkerFilter || workerId?.toString() === selectedWorkerFilter;
      })
    : reversi;

  const activeStockList = inventoryItems.filter(
    (p) =>
      (p.stockKind || 'WAREHOUSE') === 'WAREHOUSE' &&
      Number(p.availableQuantity ?? p.currentQuantity ?? 0) > 0
  );

  const selectedStockItem =
    inventoryItems.find((p) => p._id === formData.itemId || p.id === formData.itemId) || null;

  const selectedPartQuantity = Number(
    selectedStockItem?.availableQuantity ?? selectedStockItem?.currentQuantity ?? 0
  );

  const getPartStockQty = (part) =>
    Number(part.availableQuantity ?? part.currentQuantity ?? 0);

  const getPartCode = (part) => part.itemCode;

  const requestedQuantity = Number(formData.quantity) || 0;

  const normalizedItemSearchTerm = itemSearchTerm.trim().toLowerCase();
  const itemsForSelect = normalizedItemSearchTerm
    ? activeStockList.filter((p) => {
        const blob = `${p.name || ''} ${p.itemCode || ''} ${p.category || ''}`.toLowerCase();
        return blob.includes(normalizedItemSearchTerm);
      })
    : activeStockList;

  const itemsTop10 = itemsForSelect.slice(0, 10);
  const itemSelectOptions = selectedStockItem
    ? (() => {
        const alreadyIncluded = itemsTop10.some(
          (p) => (p._id || p.id)?.toString() === (selectedStockItem._id || selectedStockItem.id)?.toString()
        );
        if (alreadyIncluded) return itemsTop10;
        return [selectedStockItem, ...itemsTop10].slice(0, 10);
      })()
    : itemsTop10;

  const itemSuggestions =
    normalizedItemSearchTerm && itemSearchFocused ? itemsForSelect.slice(0, 10) : [];

  const normalizedReversSearchTerm = reversSearchTerm.trim().toLowerCase();
  const adminSearchedReversi = normalizedReversSearchTerm
    ? reversi.filter((revers) => {
        const spare = getReversItemInfo(revers);
        const workerName = getWorkerName(revers.workerId);

        const blob = [
          spare?.name,
          spare?.category,
          spare?.code,
          workerName,
          revers?.notes,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return blob.includes(normalizedReversSearchTerm);
      })
    : reversi;

  const reversiByWorker = adminSearchedReversi.reduce((acc, revers) => {
    const workerId = (getWorkerIdFromRevers(revers) || 'unknown').toString();
    if (!acc[workerId]) {
      acc[workerId] = {
        workerId,
        workerName: getWorkerName(revers.workerId),
        items: []
      };
    }
    acc[workerId].items.push(revers);
    return acc;
  }, {});
  const workerGroups = Object.values(reversiByWorker).sort((a, b) => a.workerName.localeCompare(b.workerName));

  const searchedReversi = normalizedReversSearchTerm
    ? filteredReversi.filter((revers) => {
        const spare = getReversItemInfo(revers);
        const workerName = getWorkerName(revers.workerId);
        const partsBlob = [
          spare?.name,
          spare?.category,
          spare?.code,
          revers?.notes,
          workerName
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return partsBlob.includes(normalizedReversSearchTerm);
      })
    : filteredReversi;

  // Admin view can get very large, so limit output to 10 rows.
  const reversiForDisplay = isWorker ? searchedReversi : searchedReversi.slice(0, 10);

  if (loading || workersLoading || inventoryLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className={`${su.card} ${su.cardPad}`}>
      <div className={su.toolbar}>
        <h2 className={su.title}>Reversi</h2>
        {!isWorker && (
          <div className={`${su.searchWrap} max-w-md`}>
            <Search className={su.searchIcon} />
            <input
              type="text"
              value={reversSearchTerm}
              onChange={(e) => setReversSearchTerm(e.target.value)}
              placeholder="Pretraži deo, napomenu ili radnika..."
              className={su.searchInput}
            />
          </div>
        )}
        {!isWorker && (
          <button
            onClick={() => setShowForm(!showForm)}
            className={showForm ? su.btnGhost : su.btnPrimary}
          >
            {showForm ? (
              <>
                <XCircle className="h-3.5 w-3.5" />
                Otkaži
              </>
            ) : (
              <>
                <Plus className="h-3.5 w-3.5" />
                Novi revers
              </>
            )}
          </button>
        )}
      </div>

      {showForm && (
        <div className="mt-2.5 bg-gray-900/40 rounded-md p-2.5">
          <h3 className="text-sm font-semibold text-white mb-2">Kreiraj novi revers</h3>
          <form onSubmit={handleSubmit} className="space-y-2">
            <div className={su.grid}>
              <div className="col-span-2 md:col-span-3 lg:col-span-4">
                <div className="flex flex-col lg:flex-row lg:items-end gap-2 mb-2">
                  <div className="lg:flex-1 min-w-0">
                    <label className={su.label}>
                      Radnik <span className="text-red-400">*</span>
                    </label>
                    <select
                      value={formData.workerId}
                      onChange={(e) => setFormData({ ...formData, workerId: e.target.value })}
                      className={su.input}
                      required
                    >
                      <option value="">Izaberi radnika</option>
                      {workers.map((worker) => (
                        <option key={worker._id} value={worker._id}>
                          {`${worker.firstName || ''} ${worker.lastName || ''}`.trim() || worker.email}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex flex-col lg:flex-row lg:items-end gap-2">
                  <div className="flex-1 min-w-0">
                    <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                      Artikal iz magacina <span className="text-red-400">*</span>
                    </label>
                    <div className="flex flex-col lg:flex-row lg:items-center gap-1.5">
                      {itemSearchVisible && (
                        <div className="relative lg:flex-1 min-w-0">
                          <input
                            type="text"
                            value={itemSearchTerm}
                            onChange={(e) => setItemSearchTerm(e.target.value)}
                            placeholder="Pretraži po nazivu ili šifri..."
                            onFocus={() => setItemSearchFocused(true)}
                            onBlur={() => {
                              setTimeout(() => setItemSearchFocused(false), 120);
                            }}
                            className={su.input}
                          />

                          {itemSuggestions.length > 0 && (
                            <div className="absolute left-0 right-0 top-full z-20 mt-1 bg-gray-700 border border-gray-600 rounded-md max-h-60 overflow-y-auto">
                              {itemSuggestions.map((part) => (
                                <button
                                  key={part._id}
                                  type="button"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    const code = getPartCode(part);
                                    const nextSearch = `${part.name}${code ? ` (${code})` : ''}`;
                                    setFormData((prev) => ({
                                      ...prev,
                                      itemId: part._id
                                    }));
                                    setItemSearchTerm(nextSearch);
                                    setItemSearchFocused(false);
                                  }}
                                  className="w-full px-3 py-2 text-left text-sm text-white hover:bg-gray-600 transition-colors"
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="min-w-0">
                                      <div className="truncate font-medium">
                                        {part.name} {getPartCode(part) ? `(${getPartCode(part)})` : ''}
                                      </div>
                                      <div className="text-xs text-gray-300">
                                        Na stanju: {getPartStockQty(part)}
                                      </div>
                                    </div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      <select
                        value={formData.itemId}
                        onChange={(e) => setFormData({ ...formData, itemId: e.target.value })}
                        onFocus={() => {
                          setItemSearchVisible(true);
                        }}
                        className={`${su.input} lg:flex-1 min-w-0`}
                      >
                        <option value="">Izaberi artikal...</option>
                        {itemSelectOptions.length === 0 ? (
                          <option value="" disabled>
                            Nema artikala na stanju
                          </option>
                        ) : (
                          itemSelectOptions.map((part) => (
                            <option key={part._id} value={part._id}>
                              {part.itemCode ? `${part.itemCode} - ` : ''}{part.name} (Na stanju: {getPartStockQty(part)})
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                  </div>

                  <div className="shrink-0">
                    <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                      Količina <span className="text-red-400">*</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, quantity: Math.max(1, (Number(prev.quantity) || 1) - 1) }))}
                        className="h-8 w-8 bg-gray-700 border border-gray-600 rounded-md text-white hover:bg-gray-600"
                        aria-label="Smanji količinu"
                      >
                        -
                      </button>
                      <input
                        type="number"
                        min="1"
                        value={formData.quantity}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            setFormData({ ...formData, quantity: '' });
                            return;
                          }
                          const numValue = Number(value);
                          if (Number.isFinite(numValue) && numValue >= 1) {
                            setFormData({ ...formData, quantity: Math.floor(numValue) });
                          }
                        }}
                        onBlur={(e) => {
                          if (e.target.value === '' || Number(e.target.value) < 1) {
                            setFormData({ ...formData, quantity: 1 });
                          }
                        }}
                        className={`${su.input} w-16 text-center ${
                          selectedStockItem && requestedQuantity > selectedPartQuantity
                            ? 'border-red-500 focus:ring-red-500'
                            : ''
                        }`}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, quantity: (Number(prev.quantity) || 0) + 1 }))}
                        className="h-8 w-8 bg-gray-700 border border-gray-600 rounded-md text-white hover:bg-gray-600"
                        aria-label="Povećaj količinu"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleAddPartToRevers}
                    className="shrink-0 h-8 px-3 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors text-xs font-medium whitespace-nowrap lg:mb-0"
                  >
                    Dodaj deo u revers
                  </button>
                </div>

                {selectedStockItem && requestedQuantity > selectedPartQuantity && (
                  <p className="mt-1 text-sm text-red-400">
                    Nedovoljna količina! Na stanju je {selectedPartQuantity} komada, a tražite {requestedQuantity} komada.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-[11px] font-medium text-gray-400 mb-0.5">
                  Napomena
                </label>
                <input
                  type="text"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className={su.input}
                  placeholder="Opcionalna napomena"
                />
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg p-3 border border-gray-600">
              <p className="text-sm font-medium text-gray-200 mb-2">Delovi za zaduženje ({reversParts.length})</p>
              {reversParts.length === 0 ? (
                <p className="text-xs text-gray-400">Niste dodali nijedan deo.</p>
              ) : (
                <div className="space-y-2">
                  {reversParts.map((item) => {
                    const partKey = `${REVERS_SOURCE}:${item.inventoryItemId?.toString()}`;
                    return (
                    <div key={partKey} className="flex items-center gap-2 bg-gray-700 rounded p-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">
                          <span className="text-xs text-gray-400 mr-1">Magacin:</span>
                          {item.name} {item.code ? `(${item.code})` : ''}
                        </p>
                      </div>
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateReversPartQuantity(partKey, e.target.value)}
                        className="w-20 px-2 py-1 bg-gray-600 border border-gray-500 rounded text-white text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => removeReversPart(partKey)}
                        className="px-2 py-1 bg-red-700 text-white rounded hover:bg-red-800 text-xs"
                      >
                        Ukloni
                      </button>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setFormData({ workerId: '', itemId: '', quantity: 1, notes: '' });
                  setReversParts([]);
                  setItemSearchTerm('');
                }}
                className={su.btnGhost}
              >
                Otkaži
              </button>
              <button
                type="submit"
                className={su.btnPrimary}
              >
                Kreiraj revers
              </button>
            </div>
          </form>
        </div>
      )}

      {!isWorker && (
        <div className="space-y-3">
          {workerGroups.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-gray-500 bg-gray-800 rounded-lg border border-gray-700">
              {normalizedReversSearchTerm ? 'Nema rezultata za pretragu' : 'Nema reversi zapisa'}
            </div>
          ) : (
            workerGroups.map((group) => {
              const isOpen = !!expandedWorkers[group.workerId];
              return (
                <div key={group.workerId} className="rounded-lg border border-gray-700 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setExpandedWorkers((prev) => ({ ...prev, [group.workerId]: !isOpen }))}
                    className="w-full px-4 py-3 bg-gray-700 hover:bg-gray-600 text-left flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2 text-white">
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <span className="font-medium">{group.workerName}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-300">{group.items.length} stavki</span>
                      {group.workerName === 'Nepoznat radnik' && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteUnknownWorkerRevers(group.workerId);
                          }}
                          className="inline-flex items-center px-2 py-1 bg-red-700 text-white rounded-md hover:bg-red-800 transition-colors text-xs"
                          title="Vrati sve delove i očisti zapise za nepoznatog radnika"
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Vrati sve i očisti
                        </button>
                      )}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-gray-800 shadow-sm">
                        <thead className="bg-gray-700">
                          <tr>
                            <th className="px-2.5 py-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-wide text-left">Datum preuzimanja</th>
                            <th className="px-2.5 py-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-wide text-left whitespace-nowrap">Stavka</th>
                            <th className="px-2.5 py-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-wide text-left">Skladište</th>
                            <th className="px-2.5 py-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-wide text-right">Količina</th>
                            <th className="px-2.5 py-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-wide text-left">Status</th>
                            <th className="px-2.5 py-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-wide text-left">Napomena</th>
                            <th className="px-2.5 py-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-wide text-center">Akcije</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700">
                          {group.items.map((revers) => {
                            const itemInfo = getReversItemInfo(revers);
                            return (
                            <tr key={revers._id} className="hover:bg-gray-700">
                              <td className="px-2.5 py-1.5 text-xs text-gray-300">
                                {new Date(revers.createdAt).toLocaleDateString('sr-RS')}
                              </td>
                              <td className="px-2.5 py-1.5 text-xs text-white">
                                <div className="flex items-center">
                                  <Package className="w-4 h-4 mr-2 text-gray-400" />
                                  {itemInfo.name}
                                  {itemInfo.code ? ` (${itemInfo.code})` : ''}
                                </div>
                              </td>
                              <td className="px-2.5 py-1.5 text-xs text-gray-400">{itemInfo.sourceLabel}</td>
                              <td className="px-2.5 py-1.5 text-xs text-gray-300 text-right">{revers.quantity}</td>
                              <td className="px-2.5 py-1.5 text-xs">
                                {revers.returned ? (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-900 text-green-200">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Vraćen
                                  </span>
                                ) : revers.installed ? (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-900 text-blue-200">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Ugrađen
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-900 text-yellow-200">
                                    <Clock className="w-3 h-3 mr-1" />
                                    Aktivan
                                  </span>
                                )}
                              </td>
                              <td className="px-2.5 py-1.5 text-xs text-gray-400">{revers.notes || '-'}</td>
                              <td className="px-2.5 py-1.5 text-xs text-center space-x-1">
                                {!revers.returned && !revers.installed && (
                                  <button
                                    onClick={() => handleReturn(revers._id)}
                                    className="inline-flex items-center px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-xs"
                                    title="Vrati deo na stanje"
                                  >
                                    <RotateCcw className="w-3 h-3 mr-1" />
                                    Vrati
                                  </button>
                                )}
                                {revers.returned && (
                                  <button
                                    onClick={() => handleReassign(revers._id)}
                                    className="inline-flex items-center px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-xs"
                                    title="Ponovo zaduži deo radniku"
                                  >
                                    Zaduži
                                  </button>
                                )}
                                {revers.installed && !revers.returned && (
                                  <span className="text-gray-400 text-xs">Ugrađen</span>
                                )}

                                {/* Ikonica kante za brisanje revers zapisa */}
                                <button
                                  onClick={() => handleDelete(revers._id)}
                                  className="inline-flex items-center px-2 py-1 bg-red-700 text-white rounded-md hover:bg-red-800 transition-colors text-xs"
                                  title="Obriši ovaj revers zapis"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {isWorker && (
      <div className="overflow-x-auto rounded-lg">
        <table className="min-w-full bg-gray-800 shadow-sm">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-2.5 py-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-wide text-left">Datum</th>
              <th className="px-2.5 py-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-wide text-left">Radnik</th>
              <th className="px-2.5 py-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-wide text-left">Stavka</th>
              <th className="px-2.5 py-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-wide text-left">Skladište</th>
              <th className="px-2.5 py-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-wide text-left">Kategorija</th>
              <th className="px-2.5 py-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-wide text-right">Količina</th>
              <th className="px-2.5 py-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-wide text-left">Status</th>
              <th className="px-2.5 py-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-wide text-left">Napomena</th>
              <th className="px-2.5 py-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-wide text-center">Akcije</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {reversiForDisplay.length === 0 ? (
              <tr>
                <td colSpan="9" className="px-3 py-6 text-center text-xs text-gray-500">
                  {normalizedReversSearchTerm
                    ? 'Nema rezultata za pretragu'
                    : selectedWorkerFilter
                    ? 'Nema reversi zapisa za izabranog radnika'
                    : 'Nema reversi zapisa'}
                </td>
              </tr>
            ) : (
              reversiForDisplay.map((revers) => {
                const itemInfo = getReversItemInfo(revers);
                return (
                <tr key={revers._id} className="hover:bg-gray-700">
                  <td className="px-2.5 py-1.5 text-xs text-gray-300">
                    {new Date(revers.createdAt).toLocaleDateString('sr-RS')}
                  </td>
                  <td className="px-2.5 py-1.5 text-xs text-white">
                    {!isWorker ? (
                      <div 
                        className="flex items-center cursor-pointer hover:text-blue-400 transition-colors"
                        onClick={() => {
                          const workerId = getWorkerIdFromRevers(revers);
                          setSelectedWorkerFilter(workerId === selectedWorkerFilter ? '' : workerId);
                        }}
                        title="Kliknite da filtrirate po ovom radniku"
                      >
                        <User className="w-4 h-4 mr-2 text-gray-400" />
                        {getWorkerName(revers.workerId)}
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <User className="w-4 h-4 mr-2 text-gray-400" />
                        {getWorkerName(revers.workerId)}
                      </div>
                    )}
                  </td>
                <td className="px-2.5 py-1.5 text-xs text-white">
                    <div className="flex items-center">
                      <Package className="w-4 h-4 mr-2 text-gray-400" />
                      {itemInfo.name}
                      {itemInfo.code ? ` (${itemInfo.code})` : ''}
                    </div>
                  </td>
                  <td className="px-2.5 py-1.5 text-xs text-gray-400">{itemInfo.sourceLabel}</td>
                  <td className="px-2.5 py-1.5 text-xs text-gray-300">
                    {itemInfo.category || '-'}
                  </td>
                  <td className="px-2.5 py-1.5 text-xs text-gray-300 text-right">{revers.quantity}</td>
                  <td className="px-2.5 py-1.5 text-xs">
                    {revers.returned ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-900 text-green-200">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Vraćen
                      </span>
                    ) : revers.installed ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-900 text-blue-200">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Ugrađen
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-900 text-yellow-200">
                        <Clock className="w-3 h-3 mr-1" />
                        Aktivan
                      </span>
                    )}
                  </td>
                  <td className="px-2.5 py-1.5 text-xs text-gray-400">{revers.notes || '-'}</td>
                  <td className="px-2.5 py-1.5 text-xs text-center">
                    {!isWorker && !revers.returned && !revers.installed && (
                      <button
                        onClick={() => handleReturn(revers._id)}
                        className="inline-flex items-center px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-xs"
                        title="Vrati deo na stanje"
                      >
                        <RotateCcw className="w-3 h-3 mr-1" />
                        Vrati
                      </button>
                    )}

                    {!isWorker && revers.installed && !revers.returned && (
                      <span className="text-gray-400 text-xs">Ugrađen</span>
                    )}

                    {isWorker && !revers.returned && !revers.installed && (
                      <span className="text-gray-400 text-xs">-</span>
                    )}

                    {isWorker && revers.installed && !revers.returned && (
                      <span className="text-gray-400 text-xs">Ugrađen</span>
                    )}

                    {revers.returned && (
                      <span className="text-gray-400 text-xs">Vraćen</span>
                    )}
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
};

export default ReversiView;

